import React, { FC, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { InfiniteScroll, Spin } from 'common/components';
import { ESocketEvent } from 'common/const/socket.enum';
import { stringToNumber } from 'common/helpers/common.helper';
import { useSearchParamsHook } from 'common/hooks/useSearchParamsHook';
import { useSocketContext } from 'common/context';
import { DEFAULT_VALUE_0 } from 'common/config';
import { ISupplyShort } from 'common/models';
import { AuthHooks } from 'common/hooks';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { RequestDetails } from 'entities/Request/components/RequestDetails';
import { RequestProgressBar } from 'entities/Request/components/RequestProgressBar';
import { RequestActions } from 'entities/Request/components/RequestActions';
import { RequestGoodsCard } from 'entities/Request/components/RequestGoodsCard';
import { RequestHeader } from 'entities/Request/components/RequestHeader';
import { RequestSupplyList } from 'entities/Request/components/RequestSupplyList';
import { RequestTitle } from 'entities/Request/components/RequestTitle';
import { RequestFooter } from 'entities/Request/components/RequestFooter';
import { IRequestFilters } from 'entities/Request/Request.models';
import { getRequestById, getRequestMembers, selectRequest } from 'entities/Request/Request.slice';
import { getSupplyById } from 'entities/Supply/Supply.slice';
import { getUserStats } from 'entities/User/User.slice';

export const RequestPage: FC = () => {
  const dispatch = useAppDispatch();
  // request
  const request = useAppSelector(selectRequest);

  const [fetchLoading, setFetchLoading] = useState<boolean>(true);
  const [filters, setFilters] = useState<IRequestFilters>({
    search: '',
    statuses: [],
  });
  const [selectedSupplies, setSelectedSupplies] = useState<ISupplyShort[]>([]);
  const [openRequestDetails, setOpenRequestDetails] = useState<boolean>(true);
  const [openRequestGoodsCard, setOpenRequestGoodsCard] = useState<boolean>(false);
  const [footerOffsetHeightPx, setFooterOffsetHeightPx] = useState<number>(DEFAULT_VALUE_0);
  const { id } = useParams();
  const { socket } = useSocketContext();
  const { getSearchParam } = useSearchParamsHook();
  const { isSeller } = AuthHooks.useIsSeller();

  const supplyId = getSearchParam('supplyId');
  const isViewMode = getSearchParam('mode') === 'view';

  const onOpenRequestGoodsCardClick = (id: number) => {
    dispatch(
      getSupplyById({
        id,
        onSuccess: () => {
          setOpenRequestGoodsCard(true);
        },
      }),
    );
  };

  useEffect(() => {
    const fetch = async () => {
      setFetchLoading(true);

      if (id) {
        const requestId = stringToNumber(id);

        setFilters({ search: '', statuses: [] });
        setSelectedSupplies([]);
        setOpenRequestGoodsCard(false);
        await dispatch(getRequestById(requestId));
        await dispatch(getRequestMembers(requestId));
      }

      setFetchLoading(false);
    };

    fetch();
  }, [id]);

  useEffect(() => {
    if (supplyId) {
      onOpenRequestGoodsCardClick(stringToNumber(supplyId));
    }
  }, [supplyId]);

  const updateUserStatsBySocket = (value: any) => {
    if (id) {
      const requestId = stringToNumber(id);

      if (requestId === value.requestId) {
        dispatch(getUserStats());
      }
    }
  };

  const updateRequestBySocket = (value: any) => {
    if (id) {
      const requestId = stringToNumber(id);

      if (requestId === value.requestId) {
        dispatch(getRequestById(stringToNumber(id)));
      }
    }
  };

  useEffect(() => {
    if (socket) {
      socket.on(ESocketEvent.Requests, updateUserStatsBySocket);
      socket.on(ESocketEvent.Supplies, updateRequestBySocket);
    }

    return () => {
      if (socket) {
        socket.off(ESocketEvent.Requests, updateUserStatsBySocket);
        socket.off(ESocketEvent.Supplies, updateRequestBySocket);
      }
    };
  }, [socket]);

  if (fetchLoading) {
    return (
      <div className="request flex align-center justify-center">
        <Spin />
      </div>
    );
  }

  if (!request) {
    return null;
  }

  return (
    <div className="redesign request">
      <div className="flex direction-column request__content">
        <InfiniteScroll>
          <RequestHeader
            request={request}
            showRequestDetailsToggle={!openRequestDetails && !openRequestGoodsCard}
            onSearch={async (value?: string) => setFilters((prev) => ({ ...prev, search: value || '' }))}
            onRequestDetailsClick={setOpenRequestDetails}
          />

          <RequestTitle request={request} isSeller={isSeller} />

          <RequestProgressBar request={request} />

          <RequestSupplyList
            isViewMode={isViewMode}
            request={request}
            filters={filters}
            selectedSupplies={selectedSupplies}
            marginBottomOffsetPx={footerOffsetHeightPx}
            onRowClick={onOpenRequestGoodsCardClick}
            onFiltersChange={setFilters}
            onSelectedSuppliesChange={setSelectedSupplies}
          />
        </InfiniteScroll>

        <RequestFooter isViewMode={isViewMode} request={request} changeFooterOffsetHeightPx={setFooterOffsetHeightPx} />
      </div>

      <RequestActions
        request={request}
        selectedSupplies={selectedSupplies}
        translateYOffsetPx={footerOffsetHeightPx}
        onSelectedSuppliesChange={setSelectedSupplies}
      />

      <div className={`request__drawers ${openRequestDetails || openRequestGoodsCard ? 'opened' : ''}`}>
        <RequestDetails open={openRequestDetails} request={request} onClose={() => setOpenRequestDetails(false)} />

        <RequestGoodsCard open={openRequestGoodsCard} request={request} onClose={() => setOpenRequestGoodsCard(false)} />
      </div>
    </div>
  );
};
